import { SaveLeadFormDraftParams } from '@plura/shared/types';
import { v4 as uuidv4 } from 'uuid';
import themes from './_themes';
import { generateCustomID } from '@plura/shared/utils';

// Export a function that generates fresh UUIDs each time it's called
const generateECommerceLeadCaptureQuizTemplate = (): SaveLeadFormDraftParams => {
  const formId = uuidv4();
  const collectionId = generateCustomID(18)
  const workspaceId = uuidv4();

  // Generate new UUIDs for all blocks to ensure uniqueness
  const welcomeScreenId = uuidv4();
  const welcomeScreenBlockId = uuidv4();
  const contactInfoId = uuidv4();
  const contactInfoBlockId = uuidv4();
  const emailId = uuidv4();
  const emailBlockId = uuidv4();
  const multipleChoice1Id = uuidv4();
  const multipleChoice1BlockId = uuidv4();
  const multipleChoice2Id = uuidv4();
  const multipleChoice2BlockId = uuidv4();
  const dateId = uuidv4();
  const dateBlockId = uuidv4();
  const endScreenId = uuidv4();
  const endScreenBlockId = uuidv4();

  // Generate new UUIDs for select options
  const mc1Option1Id = uuidv4();
  const mc1Option2Id = uuidv4();
  const mc1Option3Id = uuidv4();
  const mc2Option1Id = uuidv4();
  const mc2Option2Id = uuidv4();
  const mc2Option3Id = uuidv4();

  // Generate new UUIDs for media
  const welcomeMediaId = uuidv4();
  const multipleChoice1MediaId = uuidv4();
  const endScreenMediaId = uuidv4();

  return {
    ...themes.coralMintBold,
    "collectionId": collectionId,
    "name": "E-Commerce Lead Generation Form",
    "overview": "This is a form to collect leads",
    "about": "We're looking to connect with new leads",
    "tone": "Casual + Professional",
    "hideBuiltWithBadge": false,
    "closed": false,
    "closedMessage": "",
    "legalDisclaimer": false,
    "legalMessage": "",
    "notifications": false,
    "notificationEmail": "",
    "logoUrl": "https://s3.amazonaws.com/leadrr-prod-uploads/uploads/0d5b697e-e386-46ef-a2c7-34044737a773/GOLDLEAF-CO-black.png",
    "transitionType": "SLIDE_VERTICAL",
    "transitionSpeed": 500,
    "skipWelcomeScreen": false,
    "googleTagManager": "",
    "metaPixel": "",
    "assistantId": `asst_${collectionId}`,
    "fontSizeSetting": "MEDIUM",
    "customEndScreen": false,
    "hiddenFields": [],
    "journeyItems": [
      {
        "type": "WELCOME_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": welcomeScreenId,
        "blockId": welcomeScreenBlockId,
        "content": "Want 20% off your next order?",
        "manditory": false,
        "order": 0,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Join our list and get an exclusive discount plus early access to new collections.",
        "thirdContent": "Sign up",
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "PEXEL",
          "id": welcomeMediaId,
          "type": "video",
          "name": "7578550",
          "link": "https://videos.pexels.com/video-files/7578550/7578550-hd_1280_720_30fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/7578550/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "CONTACT_INFO",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": contactInfoId,
        "blockId": contactInfoBlockId,
        "content": "Let's start with your name.",
        "manditory": false,
        "order": 1,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll keep things personal.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": {
          "first_name": {
            "required": false,
            "visible": true
          },
          "last_name": {
            "required": false,
            "visible": false
          },
          "email": {
            "required": false,
            "visible": false
          },
          "phone": {
            "required": false,
            "visible": false
          },
          "company": {
            "required": false,
            "visible": false
          }
        },
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "EMAIL",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": emailId,
        "blockId": emailBlockId,
        "content": `And your email, {{${contactInfoBlockId}__first_name}}?`,
        "manditory": false,
        "order": 2,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "We'll send your discount straight to your inbox.",
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "DETATCHED_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice1Id,
        "blockId": multipleChoice1BlockId,
        "content": "What home items do you shop for most often?",
        "manditory": false,
        "order": 3,
        "selectOptions": [
          {
            "id": mc1Option1Id,
            "order": 0,
            "value": "What home items do you shop for most often?"
          },
          {
            "id": mc1Option2Id,
            "order": 1,
            "value": "Premium pillowcases"
          },
          {
            "id": mc1Option3Id,
            "order": 2,
            "value": "Soft duvet covers"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "UNSPLASH",
          "id": multipleChoice1MediaId,
          "type": "image",
          "name": "white bed pillow on bed",
          "link": "https://unsplash.com/photos/EoTUCbv9Jrs/download?ixid=M3w1Nzk2MDF8MHwxfHNlYXJjaHwxMXx8YmVkcm9vbXxlbnwwfHx8fDE3NjQ0MTE0ODR8MA",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date()
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "MULTIPLE_CHOICE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": multipleChoice2Id,
        "blockId": multipleChoice2BlockId,
        "content": "How often do you shop for home essentials?",
        "manditory": false,
        "order": 4,
        "selectOptions": [
          {
            "id": mc2Option1Id,
            "order": 0,
            "value": "Only when I really need to"
          },
          {
            "id": mc2Option2Id,
            "order": 1,
            "value": "A few times a year"
          },
          {
            "id": mc2Option3Id,
            "order": 2,
            "value": "I love updating things regularly"
          }
        ],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": null,
        "thirdContent": null,
        "forthContent": null,
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "DATE",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "INLINE_CENTER",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": dateId,
        "blockId": dateBlockId,
        "content": "Oh also, when's your birthday?",
        "manditory": false,
        "order": 5,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "MMDDYYYY",
        "thirdContent": "/",
        "forthContent": "We love sending a little something when your day comes around.",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": false,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": null,
        "media": null,
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      },
      {
        "type": "END_SCREEN",
        "strictness": "COMMON",
        "contentAlignment": "LEFT",
        "mediaPosition": "SPLIT_RIGHT",
        "mobileMediaPosition": "MOBILE_POSTER_TOP",
        "id": endScreenId,
        "blockId": endScreenBlockId,
        "content": "You're all set!",
        "manditory": false,
        "order": 6,
        "selectOptions": [],
        "multiSelect": null,
        "youtubeVideoURL": null,
        "internalName": null,
        "consentTitle": null,
        "minRange": null,
        "maxRange": null,
        "minLabel": null,
        "maxLabel": null,
        "secondaryContent": "Your exclusive 20% code is on its way.",
        "thirdContent": "Keep browsing",
        "forthContent": "https://www.formsuite.co",
        "icon": null,
        "additionalSettings": null,
        "targetBlockId": null,
        "endOnCompletion": true,
        "scoreValues": null,
        "responseTableVisible": null,
        "opacity": 100,
        "media": {
          "provider": "PEXEL",
          "id": endScreenMediaId,
          "type": "video",
          "name": "3196248",
          "link": "https://videos.pexels.com/video-files/3196248/3196248-hd_1280_720_25fps.mp4",
          "workspaceId": workspaceId,
          "createdAt": new Date(),
          "updatedAt": new Date(),
          "thumbnail": "https://images.pexels.com/videos/3196248/pictures/preview-0.jpg"
        },
        "createdAt": new Date(),
        "updatedAt": new Date(),
        "formId": formId,
        "logicItems": [],
        "horizontalAlignment": true,
      }
    ],
    "snippets": [],
    "metaTitle": "E-Commerce Lead Generation Form",
    "gdprSetting": "NEVER",
    "gdprRequired": false,
    "consentMessageTitle": "I consent to the collection of my personal data"
  };
}

export default generateECommerceLeadCaptureQuizTemplate;